import { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useGroupSubscriptionsStore } from '../stores/groupSubscriptionsStore';

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M20 21V19C20 16.8 18.2 15 16 15H8C5.8 15 4 16.8 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 11C14.209 11 16 9.209 16 7C16 4.791 14.209 3 12 3C9.791 3 8 4.791 8 7C8 9.209 9.791 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const StarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M19 19L14.667 14.667M17 9.5C17 13.642 13.642 17 9.5 17C5.358 17 2 13.642 2 9.5C2 5.358 5.358 2 9.5 2C13.642 2 17 5.358 17 9.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MetricCard = ({ title, value, subValue, icon: Icon }) => (
  <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
    <div className="text-text-secondary mb-4"><Icon /></div>
    <div className="text-3xl font-bold text-text-primary mb-1">{value}</div>
    <div className="text-sm text-text-secondary">{title}</div>
    {subValue && <div className="text-xs text-text-muted mt-2">{subValue}</div>}
  </div>
);

const PeriodSelector = ({ period, onChange }) => {
  const periods = [
    { value: '7days', label: '7 дней' },
    { value: '30days', label: '30 дней' },
    { value: '90days', label: '90 дней' },
  ];

  return (
    <div className="flex gap-2">
      {periods.map((item) => (
        <button
          key={item.value}
          onClick={() => onChange(item.value)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            period === item.value
              ? 'bg-accent-primary text-white'
              : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const GroupSubscriptions = () => {
  const {
    summary,
    isLoadingSummary,
    chartData,
    chartPeriod,
    isLoadingCharts,
    users,
    usersTotal,
    usersPage,
    usersLimit,
    searchQuery,
    isLoadingUsers,
    error,
    fetchSummary,
    fetchCharts,
    fetchUsers,
  } = useGroupSubscriptionsStore();

  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchSummary();
    fetchCharts();
    fetchUsers({ page: 1 });
  }, []);

  useEffect(() => {
    setSearch(searchQuery);
  }, [searchQuery]);

  const handleSearch = (event) => {
    event.preventDefault();
    fetchUsers({ page: 1, search: search.trim() });
  };

  const handleReset = () => {
    setSearch('');
    fetchUsers({ page: 1, search: '' });
  };

  const totalPages = Math.max(1, Math.ceil(usersTotal / usersLimit));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Подписки на сообщество</h1>
        <p className="text-sm text-text-secondary mt-1">
          Пользователи, подписавшиеся на сообщество и получившие +20 звезд
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {isLoadingSummary ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="h-40 bg-bg-secondary rounded-xl border border-border-color animate-pulse" />
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            title="Всего подписалось"
            value={summary.total}
            subValue={`Сегодня: ${summary.today}`}
            icon={UserIcon}
          />
          <MetricCard
            title="За 30 дней"
            value={summary.last_30_days}
            subValue={`Звезд: ${summary.last_30_days_stars} ⭐`}
            icon={StarIcon}
          />
          <MetricCard
            title="Выдано звезд"
            value={`${summary.total_stars} ⭐`}
            subValue={`Сегодня: ${summary.today_stars} ⭐`}
            icon={StarIcon}
          />
        </div>
      ) : null}

      <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold text-text-primary">Динамика подписок</h2>
          <PeriodSelector period={chartPeriod} onChange={fetchCharts} />
        </div>

        {isLoadingCharts ? (
          <div className="h-72 flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-2 border-accent-primary border-t-transparent rounded-full" />
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="date"
                stroke="rgba(255,255,255,0.5)"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
                }}
              />
              <YAxis stroke="rgba(255,255,255,0.5)" tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(31, 41, 55, 0.95)',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                labelFormatter={(value) => new Date(value).toLocaleDateString('ru-RU')}
                formatter={(value, name) => name === 'count' ? [`${value} подписок`, 'Подписки'] : [value, name]}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ fill: '#22c55e', r: 4 }}
                name="count"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-72 flex items-center justify-center text-text-secondary">
            Нет данных для отображения
          </div>
        )}
      </div>

      <div className="bg-bg-secondary rounded-xl border border-border-color">
        <div className="p-6 border-b border-border-color">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-text-primary">Подписавшиеся</h2>
              <p className="text-sm text-text-secondary mt-1">Всего записей: {usersTotal}</p>
            </div>
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">
                  <SearchIcon />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="VK ID или ID пользователя"
                  className="w-64 pl-10 pr-4 py-2.5 bg-bg-tertiary border border-border-color rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:border-accent-primary"
                />
              </div>
              <button type="submit" className="px-4 py-2.5 bg-accent-primary text-white font-medium rounded-lg hover:bg-accent-primary/90 transition-colors">
                Найти
              </button>
              {searchQuery && (
                <button type="button" onClick={handleReset} className="px-4 py-2.5 bg-bg-tertiary text-text-secondary font-medium rounded-lg hover:text-text-primary transition-colors">
                  Сбросить
                </button>
              )}
            </form>
          </div>
        </div>

        {isLoadingUsers ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className="h-16 bg-bg-tertiary rounded-lg animate-pulse" />
            ))}
          </div>
        ) : users.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-bg-tertiary border-b border-border-color">
                  <tr>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">VK ID</th>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Дата подписки</th>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Награда</th>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Баланс</th>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Активность</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-color">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-bg-tertiary/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-sm font-mono text-text-primary">{user.vk_user_id}</div>
                        {user.registered_at && <div className="text-xs text-text-muted">Регистрация: {formatDate(user.registered_at)}</div>}
                      </td>
                      <td className="px-6 py-4 text-sm text-text-secondary">{formatDate(user.subscribed_at)}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-accent-primary">{user.reward_amount} ⭐</td>
                      <td className="px-6 py-4 text-sm text-text-secondary">{user.balance ?? '—'}</td>
                      <td className="px-6 py-4 text-sm text-text-secondary">
                        <div>Чатов: {user.chats_count}</div>
                        <div className="text-xs text-text-muted">Сообщений: {user.messages_count}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between p-6 border-t border-border-color">
              <div className="text-sm text-text-secondary">
                Страница {usersPage} из {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchUsers({ page: usersPage - 1 })}
                  disabled={usersPage <= 1}
                  className="px-4 py-2 bg-bg-tertiary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-primary transition-colors"
                >
                  Назад
                </button>
                <button
                  onClick={() => fetchUsers({ page: usersPage + 1 })}
                  disabled={usersPage >= totalPages}
                  className="px-4 py-2 bg-bg-tertiary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-primary transition-colors"
                >
                  Вперёд
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center text-text-secondary py-10">Нет данных</div>
        )}
      </div>
    </div>
  );
};
